<script setup>
/* Imports */
import {
  reactive,
  computed,
  ref,
  onMounted,
  watch,
} from 'vue';

/* Store */
import { storeToRefs } from 'pinia';
import useGlobalStore from '../store/globalStore.js';

/* Helpers */
import { translateWithKeys } from '../helpers/translate.js';

/* Props */
const props = defineProps({
  course: {
    type: Object,
    default: () => {},
  },
});

/* Store */
const globalStore = useGlobalStore();
const {
  translations,
  currentCountry,
  countries,
} = storeToRefs(globalStore);

const state = reactive({
  shouldUseMapsApplication: false,
  shouldOpenMapsInNewTab: false,
  holes: computed(() => translateWithKeys(translations.value.holes, {
    NumberOfHoles: props.course.holes,
  })),
  target: computed(() => (state.shouldOpenMapsInNewTab ? '_blank' : '_self')),
});

const addressLine2 = computed(() => {
  const countryString = (currentCountry.value ? ', ' : '') + currentCountry.value;
  if (props.course.city) {
    return `${props.course.city}, ${props.course.state}${countryString}`;
  }
  return `${props.course.state}${countryString}`;
});

const getCourseName = (course) => {
  if (!course.localName) return course.name;
  return `${course.localName} | ${course.name}`;
};

const getCourseAddress1 = (course) => {
  if (!course.localAddress) return course.addr;
  return `${course.localAddress} | ${course.addr}`;
};

const getCourseAddress2 = (course) => {
  const countryString = (countries.value[course.a2]?.length ? ', ' : '') + (countries.value[course.a2] || '');

  let result;
  let localResult;

  if (course.city) {
    result = `${course.city}, ${course.state}${countryString}`;
    localResult = `${course.localCity}, ${course.localState}`;
  } else {
    result = `${course.state}${countryString}`;
    localResult = `${course.localState}${countryString}`;
  }

  if (props.course.localAddress) {
    return `${localResult} | ${result}`;
  }

  return result;
};

const getAddressFull = (course) => `${getCourseAddress1(course)}, ${getCourseAddress2(course)}`;

const copyAddress = () => {
  let { addr } = props.course;
  if (addr) addr += ', ';
  addr += addressLine2.value;

  if (!state.shouldUseMapsApplication && navigator.clipboard) {
    navigator.clipboard.writeText(addr).catch((e) => {
      /* eslint-disable no-console */
      console.error(`Failed to copy to clipboard: ${e}`);
    });
    document.dispatchEvent(new ClipboardEvent('copy'));
  }
};

const mapLink = computed(() => {
  // Universal fallback (works on any device, SSR-safe) — Google Maps web search.
  const buildGoogleMapsWebUrl = () => {
    if (props.course.addr) {
      const mapString = encodeURIComponent(getAddressFull(props.course));
      return `https://www.google.com/maps/search/?api=1&query=${mapString}`;
    }
    return `https://www.google.com/maps/search/?api=1&query=${props.course.ll[0]}%2C${props.course.ll[1]}`;
  };

  if (typeof window === 'undefined') {
    // On the server we can't sniff the platform, but we must render a valid
    // href so the card CTA works in the pre-hydration HTML (crawlers,
    // no-JS users, CDN-cached responses).
    state.shouldOpenMapsInNewTab = true;
    return buildGoogleMapsWebUrl();
  }

  const shouldUseAppleMaps = [
    'Macintosh',
    'MacIntel',
    'MacPPC',
    'iPhone',
    'iPad',
    'iPod',
  ].some((p) => p === window.navigator.platform);
  const shouldUseGoogleMaps = (
    ['Android'].some((p) => p === window.navigator.platform)
    || window.navigator.userAgent.toLowerCase().includes('android')
  );

  /* eslint vue/no-side-effects-in-computed-properties: 0 */
  state.shouldUseMapsApplication = shouldUseAppleMaps || shouldUseGoogleMaps;

  if (shouldUseAppleMaps) {
    state.shouldOpenMapsInNewTab = false;
    return `https://maps.apple.com/?address=${props.course.ll[0]}%2C${props.course.ll[1]}`;
  } if (shouldUseGoogleMaps) {
    state.shouldOpenMapsInNewTab = true;
    if (props.course.addr) {
      const mapString = encodeURIComponent(getAddressFull(props.course));
      return `https://www.google.com/maps/search/?api=1&query=${mapString}`;
    }
    return `https://www.google.com/maps/search/?api=1&query=${props.course.ll[0]}%2C${props.course.ll[1]}`;
  }
  return buildGoogleMapsWebUrl();
});

const cardProps = computed(() => ({
  linkUrl: mapLink.value,
  target: state.target,
  width: 'third',
  onClick: !state.shouldUseMapsApplication ? copyAddress : undefined,
}));

/* Stencil reads the `card` and `dataTestId` objects as DOM properties on
   <g-card>. Set them here directly so they survive the production build, where
   Vue doesn't attach the `__vueParentComponent` back-reference the client entry
   would otherwise need to recover them. Because the <g-*> web components are
   registered after the app mounts (see entry-client.js), Vue's hydration pass
   never applies object props as DOM properties, so `data-testid` attributes
   would otherwise be missing. */
const cardTestIds = {
  host: 'courses-card',
  tag: 'courses-card-wrapper',
  cardContent: 'courses-card-content',
};
const cardEl = ref(null);
const applyCard = () => {
  if (cardEl.value) {
    cardEl.value.card = cardProps.value;
    cardEl.value.dataTestId = cardTestIds;
  }
};
onMounted(applyCard);
watch(cardProps, applyCard);
</script>

<template>
  <g-card
    ref="cardEl"
    :card="cardProps"
    :dataTestId="cardTestIds"
  >
    <g-heading
      slot="heading"
      size="3"
      :dataTestId="{
        host: 'courses-card-heading',
      }"
      class="courses-card__heading"
    >
      <h2
        data-testid="courses-card-title"
        v-html="getCourseName(props.course)"
      />
    </g-heading>

    <div
      slot="copy"
      data-testid="courses-card-copy-wrapper"
    >
      <span
        data-testid="courses-card-address-line-1"
        v-text="getCourseAddress1(props.course)"
      />

      <g-spacer
        height="1"
        color="transparent"
        :dataTestId="{
          host: 'courses-card-spacer',
        }"
      />

      <span
        data-testid="courses-card-address-line-2"
        v-text="getCourseAddress2(props.course)"
      />
    </div>

    <div
      slot="disclaimer"
      data-testid="courses-card-badges-wrapper"
    >
      <div
        class="courses-card__badges"
        data-testid="courses-card-badges"
      >
        <div
          class="courses-card__badge courses-card__badge--holes"
          data-testid="courses-card-badge-holes"
        >
          {{ state.holes }}
        </div>

        <div
          v-if="course.upd"
          class="courses-card__badge courses-card__badge--updated"
          data-testid="courses-card-badge-updated"
        >
          {{ translations.updated }}
        </div>

        <div
          v-if="course.new"
          class="courses-card__badge courses-card__badge--new"
          data-testid="courses-card-badge-new"
        >
          {{ translations.new }}
        </div>

        <div
          v-if="course.contours"
          class="courses-card__badge courses-card__badge--contours"
          data-testid="courses-card-badge-contours"
        >
          {{ translations.contours }}
        </div>
      </div>
    </div>
  </g-card>
</template>

<style lang="scss" scoped>
.courses-card {

  &__badges {
    display: flex;
    flex-wrap: wrap;
    @include spacing('margin-top', 'sm');
    text-transform: uppercase;
    gap: $spacing-sm;
  }

  &__badge {
    border-radius: 2px;
    font-size: 0.85rem;
    @include font-primary-weight-medium();
    line-height: 1.1875rem;
    background: $color-gray;
    padding: ($spacing-sm) ($spacing-md);

    &--updated,
    &--new {
      background: $color-candy-blue;
      color: $color-black;
    }

    &--contours {
      background: $color-green-50;
    }
  }

  &__heading.g__heading {
    h2 {
      @include spacing('margin-bottom', 'none');
    }
  }
}

:deep(.g__card__content .copy) {
  flex: 1;
}
</style>
